import React, { FC, createContext, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useBorrowStatus } from '@/features/Pools/ui/UserBorrowSection/hooks/useBorrowStatus';
import { useUserBorrowItems } from '@/features/Pools/ui/UserBorrowSection/hooks/useUserBorrowItems';
import { useFetchUserBorrows } from '@/features/Pools/ui/UserBorrowSection/hooks/useFetchUserBorrows';
import { Store } from '@/shared/store';
import { BorrowStatusType, ROLE } from '@/shared/api/borrow';
import { UserBorrowItem } from '@/features/Pools';

type MarginAccountsProviderProps = {
    children: React.ReactNode;
};

export const MarginAccountsContext = createContext<{
    items: UserBorrowItem[];
    selectedBorrowStatusList: BorrowStatusType;
    handleSelectedBorrowStatusListChange: (value: string | number) => void;
    callUserBorrows: () => void;
}>({
    items: [],
    selectedBorrowStatusList: BorrowStatusType.OPENED,
    handleSelectedBorrowStatusListChange: () => {},
    callUserBorrows: () => {},
});

export const MarginAccountsProvider: FC<MarginAccountsProviderProps> = ({ children }) => {
    const { borrowAgreements } = useSelector((state: Store) => ({
        borrowAgreements: state.agreements.userBorrows,
    }));

    const { selectedBorrowStatusList, handleSelectedBorrowStatusListChange } = useBorrowStatus();

    const { items } = useUserBorrowItems(selectedBorrowStatusList);

    const { callUserBorrows } = useFetchUserBorrows(selectedBorrowStatusList);

    const isBorrowAgreementsLoaded = useMemo(() => {
        return borrowAgreements?.some((agreement) => agreement?.role === ROLE.BORROWER);
    }, [borrowAgreements]);

    useEffect(() => {
        if (isBorrowAgreementsLoaded) {
            callUserBorrows();
        }
    }, [isBorrowAgreementsLoaded]);

    return (
        <MarginAccountsContext.Provider
            value={{
                items,
                selectedBorrowStatusList,
                handleSelectedBorrowStatusListChange,
                callUserBorrows,
            }}
        >
            {children}
        </MarginAccountsContext.Provider>
    );
};

export const useMarginAccountsContext = () => useContext(MarginAccountsContext);
